import React, { useCallback, useContext, useMemo, useState } from "react";
import { PhoenixStyledTooltip } from "src/Components/Dumb/PhoenixStyledTooltip";
import { LeadCardContactButton } from "src/Components/Segments/DashboardSideBarSegments/LeadCardV2";
import { BadNumberTag } from "src/Components/Smart/BadNumberTag";
import { AppText, FlexDiv } from "src/Components/UI";
import { PhoenixIcon } from "src/Components/UI/Phoenix";
import { loggedInUser } from "src/apollo/cache";
import { CallContext, ModalContext } from "src/context";
import {
  chevron_down,
  chevron_up,
  contact_info_add,
  edit,
  merge_primary,
  user_add,
  user_white,
  users,
} from "src/images/NewDesign";
import { MixpanelActions } from "src/services/mixpanel";
import { SelectedContactType } from "src/types";
import { formatPhoneNumber } from "src/utils/format";
import { useDisableActionButton } from "src/utils/hooks";
import {
  BAD_NUMBER_DISPOSITION_TYPES,
  ON_DIAL_WARNING_TOOLTIP,
  returnLeadAlternateEmails,
  returnLeadAlternatePhoneNumbers,
} from "src/utils/misc";
import { theme } from "src/utils/theme";
import { appToast } from "src/utils/toast";
import styled from "styled-components";
import { EmailCommunicationToggle, SMSCommunicationToggle } from "../../LeadDetail";

interface IContactTab {
  leadData: any;
  intentData?: any;
  associatedContacts?: any[];
  loadingLead: boolean;
  maxHeight?: number;
  fetchLead: () => void;
}

export const ContactTab = React.memo<IContactTab>(
  ({ leadData, intentData, associatedContacts, maxHeight, loadingLead, fetchLead }) => {
    const {
      setShowAddAssociateModal,
      setPrimaryLeadToAddAssociateTo,
      setMergeLeadsData,
      setShowMergeContactsModal,
      setShowAddOrEditContactModal,
      setSelectedContactV2,
    } = useContext(ModalContext);

    const handleShowAddNewAssociateModal = useCallback(() => {
      setShowAddAssociateModal(true);
      setPrimaryLeadToAddAssociateTo(leadData?.id);
    }, [leadData]);
    const handleShowAssociateExistingModal = useCallback(() => {
      setShowMergeContactsModal(true);
      setMergeLeadsData({
        source: {
          id: leadData?.id,
          full_name: leadData?.full_name,
          business_name: leadData?.business_name,
        },
      });
    }, [leadData]);
    const handleAddContact = useCallback(() => {
      setShowAddOrEditContactModal(true);
      setSelectedContactV2({
        lead_id: leadData?.id ?? "",
        country_code: leadData?.primary_phone_number_country_code,
        card_type: "Add",
        // if the lead already has a conference number they can't add another
        has_conference_number: !!leadData?.conference_number,
      });
    }, [leadData]);

    return (
      <Wrapper maxHeight={maxHeight}>
        <Container>
          <FlexDiv gap={8} justify="center" align="center">
            <EmailCommunicationToggle
              lead={leadData}
              loadingLead={loadingLead || !leadData}
              refetchFunction={fetchLead}
            />
            <SMSCommunicationToggle
              lead={leadData}
              loadingLead={loadingLead || !leadData}
              refetchFunction={fetchLead}
            />
            <FlexDiv align="center" gap={4} style={{ cursor: "pointer" }} onClick={handleAddContact}>
              <PhoenixIcon
                svg={contact_info_add}
                size={16}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
                pointer
              />
              <AppText
                fontSize={10}
                fontWeight={600}
                lineHeight={16}
                uppercase
                letterSpacing={1}
                color={theme.text.brand.primary}
              >
                Add Contact Info
              </AppText>
            </FlexDiv>
          </FlexDiv>

          <ContactModule type="Phone" leadData={leadData} intentData={intentData} />
          <ContactModule type="Email" leadData={leadData} intentData={intentData} />
          <ContactModule type="Screen Share" leadData={leadData} intentData={intentData} />

          <AssociatedContactsContainer>
            <FlexDiv justify="space-between" align="center">
              <FlexDiv align="center" gap={8}>
                <PhoenixIcon
                  svg={users}
                  size={12}
                  color={theme.icon.neutral.hover}
                  hoverColor={theme.icon.neutral.hover}
                />
                <AppText fontSize={10} fontWeight={500} lineHeight={14} color={theme.text.neutral.tertiary}>
                  Associated Contacts
                </AppText>
              </FlexDiv>
              <FlexDiv align="center" gap={8}>
                <FlexDiv align="center" gap={4} style={{ cursor: "pointer" }} onClick={handleShowAddNewAssociateModal}>
                  <PhoenixIcon
                    svg={user_add}
                    size={16}
                    color={theme.icon.brand.default}
                    hoverColor={theme.icon.brand.default}
                    pointer
                  />
                  <AppText
                    fontSize={10}
                    fontWeight={600}
                    lineHeight={16}
                    uppercase
                    letterSpacing={1}
                    color={theme.text.brand.primary}
                  >
                    Add New
                  </AppText>
                </FlexDiv>
                <FlexDiv
                  align="center"
                  gap={4}
                  style={{ cursor: "pointer" }}
                  onClick={handleShowAssociateExistingModal}
                >
                  <PhoenixIcon
                    svg={merge_primary}
                    size={16}
                    color={theme.icon.brand.default}
                    hoverColor={theme.icon.brand.default}
                    pointer
                  />
                  <AppText
                    fontSize={10}
                    fontWeight={600}
                    lineHeight={16}
                    uppercase
                    letterSpacing={1}
                    color={theme.text.brand.primary}
                  >
                    Associate Existing
                  </AppText>
                </FlexDiv>
              </FlexDiv>
            </FlexDiv>
            {!associatedContacts?.length && (
              <AppText fontSize={10} fontWeight={500} uppercase color={theme.text.neutral.secondary}>
                No Associated Contacts Found
              </AppText>
            )}
            {associatedContacts?.map((ac: any) => (
              <AssociatedContact data={ac} key={ac.id} />
            ))}
          </AssociatedContactsContainer>
        </Container>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div<{ maxHeight?: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  width: 100%;
  height: 100%;
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : "100%")};
  padding: 16px 0px;

  overflow-y: auto;
`;

const Container = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 70%;
  max-width: 784px;
  min-width: 330px;

  padding: 16px 0px;
`;

const Segment = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  padding: 8px;

  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const ContactWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  width: 100%;
  height: fit-content;
  padding: 8px;

  border: 1px solid ${theme.border.neutral.primary};
  border-radius: 8px;

  background-color: ${theme.fill.neutral.primary};

  & ${Segment}:last-child {
    border-bottom: none;
  }
`;

export const ContactTag: React.FC<{ type: "primary" | "associate" }> = ({ type }) => {
  return (
    <ContactBox type={type}>
      <PhoenixIcon
        svg={type === "primary" ? user_white : users}
        color={theme.icon.neutral.inverse}
        hoverColor={theme.icon.neutral.inverse}
        size={12}
      />
    </ContactBox>
  );
};
const ContactBox = styled.div<{ type: "primary" | "associate" }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;

  border-radius: 3px;
  background-color: ${({ type }) => (type === "primary" ? theme.fill.brand.primary : theme.fill.brand.inverse)};
`;

const AssociatedContactsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  width: 100%;
  padding: 8px 0px;

  border-top: 1px solid ${theme.border.neutral.primary};
`;

export const AssociatedContact: React.FC<{ data: any }> = ({ data }) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const handleExpand = useCallback(() => setExpanded((prev) => !prev), [expanded, setExpanded]);

  const alternateNumbers = useMemo(() => returnLeadAlternatePhoneNumbers(data), [data]);
  const alternateEmails = useMemo(() => returnLeadAlternateEmails(data), [data]);

  const isAdmin = useMemo(() => loggedInUser()?.role === "ADMIN", []);

  const actionDisabled = useDisableActionButton(data);

  const { goToCall, resetNextDial, callState, dialAsNumber } = useContext(CallContext);
  const {
    setShowAddOrEditContactModal,
    setSelectedContactV2,
    openEmailEditorModal,
    showEmailEditorModal,
    composeEmailData,
    showSMSChat,
    openSMSChat,
  } = useContext(ModalContext);

  const goToCallObjectForPrimary = useMemo(
    () => ({
      lead_id: data?.id,
      intentId: data?.next_intent_scheduled_or_unscheduled?.id
        ? data?.next_intent_scheduled_or_unscheduled?.id
        : undefined,
      intentReplacementId: data?.next_intent_scheduled_or_unscheduled?.replacement_lead_intent_id
        ? data?.next_intent_scheduled_or_unscheduled?.replacement_lead_intent_id
        : undefined,
      dialAsNumber: dialAsNumber,
    }),
    [data],
  );

  return (
    <ContactWrapper>
      <FlexDiv justify="space-between" width="100%">
        <FlexDiv gap={8} align="center">
          <ContactTag type="associate" />
          <AppText fontSize={12} fontWeight={400} lineHeight={18}>
            {!data?.first_name && !data?.last_name ? "NA" : `${data?.first_name}` + " " + `${data?.last_name}`}
          </AppText>
        </FlexDiv>
        <PhoenixIcon
          svg={expanded ? chevron_up : chevron_down}
          size={20}
          variant="brand"
          pointer
          onClick={handleExpand}
        />
      </FlexDiv>

      {expanded && (
        <ACBody>
          <ACSegment>
            <FlexDiv align="center" gap={8}>
              <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.brand.primary}>
                Primary Phone
              </AppText>
              <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                {formatPhoneNumber(data?.primary_phone_number)}
              </AppText>
            </FlexDiv>

            {!isAdmin && (
              <FlexDiv align="center">
                <LeadCardContactButton
                  disabled={actionDisabled["manualSms"]}
                  onClick={() => {
                    if (showSMSChat) {
                      appToast("There is currently a chat open. Please close the existing chat before opening.");
                      return;
                    }
                    MixpanelActions.track("Lead Card", {
                      type: "sms clicked",
                      id: data?.id,
                    });

                    openSMSChat({
                      lead_id: data?.id ?? "",
                      phone_number: data?.primary_phone_number,
                      intent: data?.next_intent_scheduled_or_unscheduled,
                    });
                  }}
                  type="sms"
                />
                <div
                  data-tip={!!callState ? ON_DIAL_WARNING_TOOLTIP : ""}
                  data-for={!!callState ? "lead-card-tooltip" : ""}
                >
                  <LeadCardContactButton
                    disabled={actionDisabled["manualCall"]}
                    onClick={() => {
                      goToCall({
                        phoneNumber: data?.primary_phone_number,
                        ...goToCallObjectForPrimary,
                      });
                      resetNextDial();
                    }}
                    callState={callState}
                    type="phone"
                  />
                </div>
              </FlexDiv>
            )}
          </ACSegment>
          <ACSegment>
            <FlexDiv align="center" gap={8}>
              <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.brand.primary}>
                Primary Email
              </AppText>
              <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                {data?.primary_email}
              </AppText>
            </FlexDiv>

            {!isAdmin && (
              <LeadCardContactButton
                disabled={actionDisabled["manualEmail"]}
                onClick={() => {
                  !showEmailEditorModal &&
                    openEmailEditorModal({
                      lead_id: data?.id ?? "",
                      full_name: data.full_name,
                      email: data?.primary_email,
                      isManualEmailSequenceStep: data?.sequence_step?.actions?.[0]?.task === "manualEmail",
                      sequenceStepId: data?.sequence_step?.actions?.[0]?.id,
                      html: composeEmailData.lead_id === data?.id ? composeEmailData.html : "",
                    });
                }}
                type="email"
              />
            )}
          </ACSegment>

          {alternateNumbers?.map((contact: any) => (
            <ACSegment>
              <FlexDiv direction="column">
                <FlexDiv gap={8} align="center">
                  <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.brand.primary}>
                    {contact?.contact_label}
                  </AppText>
                  <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                    {formatPhoneNumber(contact?.value)}
                  </AppText>
                </FlexDiv>

                <AppText fontSize={10} fontWeight={500} lineHeight={14} color={theme.text.neutral.secondary}>
                  {contact.type}
                </AppText>
              </FlexDiv>

              {!isAdmin && (
                <FlexDiv align="center">
                  <LeadCardContactButton
                    disabled={actionDisabled["manualSms"]}
                    onClick={() => {
                      if (showSMSChat) {
                        appToast("There is currently a chat open. Please close the existing chat before opening.");
                        return;
                      }
                      MixpanelActions.track("Lead Card", {
                        type: "sms clicked",
                        id: data?.id,
                      });

                      openSMSChat({
                        lead_id: data?.id ?? "",
                        phone_number: contact?.value,
                        intent: data?.next_intent_scheduled_or_unscheduled,
                      });
                    }}
                    type="sms"
                  />
                  <div
                    data-tip={!!callState ? ON_DIAL_WARNING_TOOLTIP : ""}
                    data-for={!!callState ? "lead-card-tooltip" : ""}
                  >
                    <LeadCardContactButton
                      disabled={actionDisabled["manualCall"]}
                      onClick={() => {
                        goToCall({
                          phoneNumber: contact?.value,
                          ...goToCallObjectForPrimary,
                        });
                        resetNextDial();
                      }}
                      callState={callState}
                      type="phone"
                    />
                  </div>
                </FlexDiv>
              )}
            </ACSegment>
          ))}
          {alternateEmails?.map((contact: any) => (
            <ACSegment>
              <FlexDiv direction="column">
                <FlexDiv gap={8} align="center">
                  <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.brand.primary}>
                    {contact?.title} {contact?.channel}
                  </AppText>
                  <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                    {contact?.value}
                  </AppText>
                </FlexDiv>

                <AppText fontSize={10} fontWeight={500} lineHeight={14} color={theme.text.neutral.secondary}>
                  {contact.type}
                </AppText>
              </FlexDiv>

              {!isAdmin && (
                <LeadCardContactButton
                  disabled={actionDisabled["manualEmail"]}
                  onClick={() => {
                    !showEmailEditorModal &&
                      openEmailEditorModal({
                        lead_id: data?.id ?? "",
                        full_name: data.full_name,
                        email: contact?.value,
                        isManualEmailSequenceStep: data?.sequence_step?.actions?.[0]?.task === "manualEmail",
                        sequenceStepId: data?.sequence_step?.actions?.[0]?.id,
                        html: composeEmailData.lead_id === data?.id ? composeEmailData.html : "",
                      });
                  }}
                  type="email"
                />
              )}
            </ACSegment>
          ))}

          {data?.conference_number && (
            <ACSegment>
              <FlexDiv align="center" gap={8}>
                <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.brand.primary}>
                  Screen Sharing
                </AppText>
                <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                  {formatPhoneNumber(data?.conference_number)}
                </AppText>
              </FlexDiv>

              {!isAdmin && (
                <div
                  data-tip={!!callState ? ON_DIAL_WARNING_TOOLTIP : ""}
                  data-for={!!callState ? "lead-card-tooltip" : ""}
                >
                  <LeadCardContactButton
                    disabled={actionDisabled["manualCall"]}
                    onClick={() => {
                      goToCall({
                        phoneNumber: data?.conference_number,
                        ...goToCallObjectForPrimary,
                      });
                      resetNextDial();
                    }}
                    callState={callState}
                    type="phone"
                  />
                </div>
              )}
            </ACSegment>
          )}
        </ACBody>
      )}
    </ContactWrapper>
  );
};

const ACBody = styled.div`
  width: 100%;
  padding: 8px 0px;
`;

const ACSegment = styled.div`
  display: flex;
  justify-content: space-between;

  width: 100%;
  padding: 8px 0px 8px 8px;

  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const EditContactButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <PhoenixIcon svg={edit} size={12} variant="neutral" pointer onClick={onClick} />
);

export const ContactModule: React.FC<{ type: "Phone" | "Email" | "Screen Share"; leadData: any; intentData?: any }> = ({
  type,
  leadData,
  intentData,
}) => {
  const {
    setShowAddOrEditContactModal,
    setSelectedContactV2,
    openEmailEditorModal,
    showEmailEditorModal,
    composeEmailData,
    showSMSChat,
    openSMSChat,
  } = useContext(ModalContext);

  const { goToCall, resetNextDial, callState, dialAsNumber } = useContext(CallContext);

  const handleEditContact = useCallback(
    (selectedContact: SelectedContactType) => {
      setSelectedContactV2(selectedContact);
      setShowAddOrEditContactModal(true);
    },
    [leadData],
  );

  const isAdmin = useMemo(() => loggedInUser()?.role === "ADMIN", []);

  const actionDisabled = useDisableActionButton(leadData);

  const isBadNumber = useCallback(
    (phoneNumber: string) =>
      leadData &&
      leadData?.flagged_numbers?.some(
        (item: any) =>
          item?.phone_number === phoneNumber &&
          BAD_NUMBER_DISPOSITION_TYPES.includes(item?.lead_activity?.related_disposition?.type),
      ),
    [leadData],
  );

  const alternatePrimaryNumbers = useMemo(() => returnLeadAlternatePhoneNumbers(leadData), [leadData]);
  const alternatePrimaryEmails = useMemo(() => returnLeadAlternateEmails(leadData), [leadData]);

  const goToCallObjectForPrimary = useMemo(
    () => ({
      lead_id: leadData?.id,
      intentId: leadData?.next_intent_scheduled_or_unscheduled?.id
        ? leadData?.next_intent_scheduled_or_unscheduled?.id
        : undefined,
      intentReplacementId: leadData?.next_intent_scheduled_or_unscheduled?.replacement_lead_intent_id
        ? leadData?.next_intent_scheduled_or_unscheduled?.replacement_lead_intent_id
        : undefined,
      dialAsNumber: dialAsNumber,
    }),
    [leadData],
  );

  if (type === "Phone" && !leadData?.primary_phone_number && !alternatePrimaryNumbers?.length) return null;
  if (type === "Email" && !leadData?.primary_email && !alternatePrimaryEmails?.length) return null;
  if (type === "Screen Share" && !leadData?.conference_number) return null;

  if (type === "Screen Share")
    return (
      <ContactWrapper>
        <PhoenixStyledTooltip id="lead-card-tooltip" place="right" maxWidth={300} lineHeight={14} textAlign="left" />
        <Segment>
          <FlexDiv align="center" gap={8}>
            <AppText fontSize={12} fontWeight={400} lineHeight={17} color={theme.text.brand.primary}>
              Screen Sharing
            </AppText>
            <AppText fontSize={12} fontWeight={400} lineHeight={17}>
              {formatPhoneNumber(leadData?.conference_number)}
            </AppText>
            <EditContactButton
              onClick={() => {
                const regex = `+${leadData?.primary_phone_number_country_code ?? "1"}`;
                handleEditContact({
                  lead_id: leadData?.id ?? "",
                  category: "Primary",
                  conference_number: leadData?.conference_number?.replace(regex, "") ?? "",
                  country_code: leadData?.primary_phone_number_country_code,
                  channel: "Conference",
                  email: "",
                  title: leadData?.primary_phone_number_title,
                  type: leadData?.primary_phone_number_type,
                  country: leadData?.country,
                  card_type: "Edit",
                });
              }}
            />
          </FlexDiv>
          {!isAdmin && (
            <div
              data-tip={!!callState ? ON_DIAL_WARNING_TOOLTIP : ""}
              data-for={!!callState ? "lead-card-tooltip" : ""}
            >
              <LeadCardContactButton
                disabled={actionDisabled["manualCall"]}
                onClick={() => {
                  goToCall({
                    phoneNumber: leadData?.conference_number,
                    ...goToCallObjectForPrimary,
                  });
                  resetNextDial();
                }}
                callState={callState}
                type="phone"
              />
            </div>
          )}
        </Segment>
      </ContactWrapper>
    );

  return (
    <ContactWrapper>
      <Segment>
        <FlexDiv direction="column" gap={4}>
          <FlexDiv gap={8} align="center">
            <ContactTag type="primary" />
            <AppText fontSize={12} fontWeight={400} lineHeight={18}>
              {leadData?.full_name || "N/A"}
            </AppText>
          </FlexDiv>

          <FlexDiv gap={8} align="center">
            <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.brand.primary}>
              Primary {type}
            </AppText>
            <AppText fontSize={12} fontWeight={400} lineHeight={18}>
              {type === "Phone" ? formatPhoneNumber(leadData?.primary_phone_number) : leadData?.primary_email}
            </AppText>
            <EditContactButton
              onClick={() =>
                handleEditContact({
                  lead_id: leadData?.id ?? "",
                  id: leadData?.id ?? "",
                  category: "Primary",
                  phone_number: type === "Email" ? "" : leadData?.primary_phone_number,
                  local_phone_number: leadData?.local_primary_phone_number,
                  conference_number: leadData?.conference_number,
                  country_code: leadData?.primary_phone_number_country_code,
                  channel: type,
                  email: type === "Email" ? leadData?.primary_email : "",
                  title: type === "Email" ? leadData?.primary_email_title : leadData?.primary_phone_number_title,
                  type: type === "Email" ? leadData?.primary_email_type : leadData?.primary_phone_number_type,
                  card_type: "Edit",
                })
              }
            />
          </FlexDiv>
        </FlexDiv>

        <FlexDiv align="center">
          {!isAdmin &&
            (type === "Phone" ? (
              <>
                <LeadCardContactButton
                  disabled={actionDisabled["manualSms"]}
                  onClick={() => {
                    if (showSMSChat) {
                      appToast("There is currently a chat open. Please close the existing chat before opening.");
                      return;
                    }
                    MixpanelActions.track("Lead Card", {
                      type: "sms clicked",
                      id: leadData?.id,
                    });

                    openSMSChat({
                      lead_id: leadData?.id ?? "",
                      phone_number: leadData?.primary_phone_number,
                      intent: intentData,
                    });
                  }}
                  type="sms"
                />
                <div
                  data-tip={!!callState ? ON_DIAL_WARNING_TOOLTIP : ""}
                  data-for={!!callState ? "lead-card-tooltip" : ""}
                >
                  <LeadCardContactButton
                    disabled={actionDisabled["manualCall"]}
                    callState={callState}
                    onClick={() => {
                      goToCall({
                        phoneNumber: leadData?.primary_phone_number,
                        ...goToCallObjectForPrimary,
                      });
                      resetNextDial();
                    }}
                    type="phone"
                  />
                </div>
              </>
            ) : (
              <LeadCardContactButton
                disabled={actionDisabled["manualEmail"]}
                onClick={() => {
                  MixpanelActions.track("Lead Card", {
                    type: "email clicked (not primary)",
                    id: leadData?.id,
                  });
                  !showEmailEditorModal &&
                    openEmailEditorModal({
                      lead_id: leadData?.id ?? "",
                      full_name: leadData.full_name,
                      email: leadData?.primary_email,
                      isManualEmailSequenceStep: leadData?.sequence_step?.actions?.[0]?.task === "manualEmail",
                      sequenceStepId: leadData?.sequence_step?.actions?.[0]?.id,
                      html: composeEmailData.lead_id === leadData?.id ? composeEmailData.html : "",
                    });
                }}
                type="email"
                width={32}
                height={32}
              />
            ))}
        </FlexDiv>
      </Segment>

      {type === "Phone" &&
        alternatePrimaryNumbers?.map((contact: any) => {
          const isAlternateNumberBad = contact.channel === "Phone" && isBadNumber(contact.value);
          return (
            <Segment key={contact.id}>
              <FlexDiv direction="column" gap={4}>
                <FlexDiv gap={8} align="center">
                  <ContactTag type="primary" />
                  <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                    {leadData?.full_name || "N/A"}
                  </AppText>
                </FlexDiv>

                <FlexDiv gap={8} align="center">
                  <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.brand.primary}>
                    {contact?.contact_label}
                  </AppText>
                  <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                    {formatPhoneNumber(contact?.value)}
                  </AppText>
                  <EditContactButton
                    onClick={() => {
                      const regex = `+${leadData?.primary_phone_number_country_code ?? "1"}`;
                      handleEditContact({
                        lead_id: leadData?.id ?? "",
                        id: contact.id,
                        category: "Alternate",
                        phone_number: contact?.channel === "Phone" ? contact.value : "",
                        local_phone_number: contact?.channel === "Phone" ? contact.value.replace(regex, "") : "",
                        country_code: leadData?.primary_phone_number_country_code,
                        email: contact?.channel === "Email" ? contact.value : "",
                        channel: contact?.channel,
                        title: contact.title,
                        type: contact.type,
                        card_type: "Edit",
                      });
                    }}
                  />
                </FlexDiv>
                <AppText fontSize={10} fontWeight={500} lineHeight={14} color={theme.text.neutral.secondary}>
                  {contact.type}
                </AppText>
                {isAlternateNumberBad && (
                  <BadNumberTag
                    canRemoveFlag={loggedInUser().role === "ADMIN"}
                    flaggedNumbers={leadData?.flagged_numbers}
                    phoneNumber={contact.value}
                  />
                )}
              </FlexDiv>

              <FlexDiv align="center">
                {!isAdmin && (
                  <>
                    <LeadCardContactButton
                      disabled={actionDisabled["manualSms"]}
                      onClick={() => {
                        if (showSMSChat) {
                          appToast("There is currently a chat open. Please close the existing chat before opening.");
                          return;
                        }
                        MixpanelActions.track("Lead Card", {
                          type: "sms clicked",
                          id: leadData?.id,
                        });

                        openSMSChat({
                          lead_id: leadData?.id ?? "",
                          phone_number: contact?.value,
                          intent: intentData,
                        });
                      }}
                      type="sms"
                    />
                    <div
                      data-tip={!!callState ? ON_DIAL_WARNING_TOOLTIP : ""}
                      data-for={!!callState ? "lead-card-tooltip" : ""}
                    >
                      <LeadCardContactButton
                        disabled={actionDisabled["manualCall"]}
                        callState={callState}
                        onClick={() => {
                          goToCall({
                            phoneNumber: contact?.value,
                            ...goToCallObjectForPrimary,
                          });
                          resetNextDial();
                        }}
                        type="phone"
                      />
                    </div>
                  </>
                )}
              </FlexDiv>
            </Segment>
          );
        })}

      {type === "Email" &&
        alternatePrimaryEmails?.map((contact: any) => (
          <Segment key={contact.id}>
            <FlexDiv direction="column" gap={4}>
              <FlexDiv gap={8} align="center">
                <ContactTag type="primary" />
                <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                  {leadData?.full_name || "N/A"}
                </AppText>
              </FlexDiv>

              <FlexDiv gap={8} align="center">
                <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.brand.primary}>
                  {contact?.title} {contact?.channel}
                </AppText>
                <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                  {contact?.value}
                </AppText>
                <EditContactButton
                  onClick={() => {
                    const regex = `+${leadData?.primary_phone_number_country_code ?? "1"}`;
                    handleEditContact({
                      lead_id: leadData?.id ?? "",
                      id: contact.id,
                      category: "Alternate",
                      phone_number: contact?.channel === "Phone" ? contact.value : "",
                      local_phone_number: contact?.channel === "Phone" ? contact.value.replace(regex, "") : "",
                      country_code: leadData?.primary_phone_number_country_code,
                      email: contact?.channel === "Email" ? contact.value : "",
                      channel: contact?.channel,
                      title: contact.title,
                      type: contact.type,
                      card_type: "Edit",
                    });
                  }}
                />
              </FlexDiv>
              <AppText fontSize={10} fontWeight={500} lineHeight={14} color={theme.text.neutral.secondary}>
                {contact.type}
              </AppText>
            </FlexDiv>

            {!isAdmin && (
              <LeadCardContactButton
                disabled={actionDisabled["manualEmail"]}
                onClick={() => {
                  MixpanelActions.track("Lead Card", {
                    type: "email clicked (not primary)",
                    id: leadData?.id,
                  });
                  !showEmailEditorModal &&
                    openEmailEditorModal({
                      lead_id: leadData?.id ?? "",
                      full_name: leadData.full_name,
                      email: contact?.value,
                      isManualEmailSequenceStep: leadData?.sequence_step?.actions?.[0]?.task === "manualEmail",
                      sequenceStepId: leadData?.sequence_step?.actions?.[0]?.id,
                      html: composeEmailData.lead_id === leadData?.id ? composeEmailData.html : "",
                    });
                }}
                type="email"
                width={32}
                height={32}
              />
            )}
          </Segment>
        ))}
    </ContactWrapper>
  );
};
